import { useId, useRef, useState } from "react";
import {
  parentCandidateDetailsSchema,
  type CanvasAssignmentCandidateInput,
  type ParentCandidateDetailsSnapshot,
  type SaveCandidateDetailsCommand,
} from "@vsta/contracts";

export function effectiveCandidate(candidate: CanvasAssignmentCandidateInput) {
  return { ...candidate, ...candidate.parentDetails?.values };
}

function deviceDate(instant: string | undefined) {
  if (instant === undefined) return "";
  const value = new Date(instant);
  return new Date(value.getTime() - value.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

export function CandidateDetails({
  candidate,
  onSave,
}: {
  candidate: CanvasAssignmentCandidateInput;
  onSave: (
    command: SaveCandidateDetailsCommand,
  ) => Promise<ParentCandidateDetailsSnapshot>;
}) {
  const id = useId();
  const [saved, setSaved] = useState(candidate.parentDetails);
  const [notes, setNotes] = useState(saved?.values.notes ?? "");
  const [title, setTitle] = useState(saved?.values.title ?? "");
  const [minutes, setMinutes] = useState(
    saved?.values.checkpointMinutes?.toString() ?? "",
  );
  const [due, setDue] = useState(deviceDate(saved?.values.dueAt));
  const [url, setUrl] = useState(saved?.values.sourceUrl ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const pending = useRef<{
    fingerprint: string;
    command: SaveCandidateDetailsCommand;
  } | null>(null);
  async function save() {
    setError(null);
    setMessage(null);
    const parsed = parentCandidateDetailsSchema.safeParse({
      ...(notes.trim() === "" ? {} : { notes: notes.trim() }),
      ...(title.trim() === "" ? {} : { title: title.trim() }),
      ...(minutes === "" ? {} : { checkpointMinutes: Number(minutes) }),
      ...(due === "" ? {} : { dueAt: new Date(due).toISOString() }),
      ...(url.trim() === "" ? {} : { sourceUrl: url.trim() }),
    });
    if (!parsed.success) {
      setError(
        "Check your entries: use 1–180 checkpoint minutes and a complete http or https link. Notes can contain up to 4,000 characters.",
      );
      return;
    }
    const fingerprint = JSON.stringify({
      details: parsed.data,
      revision: saved?.revision ?? 0,
    });
    if (pending.current?.fingerprint !== fingerprint)
      pending.current = {
        fingerprint,
        command: {
          requestId: crypto.randomUUID(),
          sourceItemKey: candidate.sourceItemKey,
          expectedRevision: saved?.revision ?? 0,
          details: parsed.data,
        },
      };
    setBusy(true);
    try {
      const result = await onSave(pending.current.command);
      setSaved(result);
      pending.current = null;
      setMessage(
        "Parent details saved. Running timers and recorded work are unchanged. New checkpoint settings apply the next time this activity starts.",
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Parent details could not be saved. Your entries are still here.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="candidate-details">
      <summary>Add or edit parent details</summary>
      <p>
        Optional: add guidance now or while the student works. Blank fields use
        the imported information. Saving preserves the source and never restarts
        a timer.
      </p>
      <p>Imported title: {candidate.title}</p>
      <fieldset disabled={busy}>
        <legend>Parent details for {candidate.title}</legend>
        <label htmlFor={`${id}-title`}>Title override</label>
        <input
          id={`${id}-title`}
          value={title}
          maxLength={300}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={candidate.title}
        />
        <label htmlFor={`${id}-notes`}>Notes and instructions</label>
        <textarea
          id={`${id}-notes`}
          value={notes}
          maxLength={4000}
          rows={3}
          onChange={(event) => setNotes(event.target.value)}
        />
        <label htmlFor={`${id}-minutes`}>Checkpoint minutes</label>
        <input
          id={`${id}-minutes`}
          type="number"
          min={1}
          max={180}
          value={minutes}
          onChange={(event) => setMinutes(event.target.value)}
        />
        <label htmlFor={`${id}-due`}>
          Due date and time override (your device time)
        </label>
        <input
          id={`${id}-due`}
          type="datetime-local"
          value={due}
          onChange={(event) => setDue(event.target.value)}
        />
        <label htmlFor={`${id}-url`}>Schoolwork link override</label>
        <input
          id={`${id}-url`}
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder={candidate.sourceUrl ?? "https://"}
        />
        <button type="button" onClick={() => void save()}>
          {busy ? "Saving parent details…" : "Save parent details"}
        </button>
      </fieldset>
      {error === null ? null : <p role="alert">{error}</p>}
      {message === null ? null : <p role="status">{message}</p>}
    </details>
  );
}
